import { useParams, Link } from "react-router-dom";
import { Check, ArrowLeft, ShieldCheck, Download, RefreshCw } from "lucide-react";
import SEO from "../components/seo/SEO";
import Breadcrumbs from "../components/seo/Breadcrumbs";
import { getProduct, PRODUCTS } from "../config/products";
import { ProductCard } from "../components/product/ProductCard";
import { BuyButton } from "../components/product/BuyButton";
import EmailSignup from "../components/common/EmailSignup";
import { productSchema } from "../lib/seo";
import { CAD } from "../lib/format";
import NotFound from "./NotFound";

export default function ProductPage() {
  const { productId } = useParams();
  const product = getProduct(productId);
  if (!product) return <NotFound />;
  const others = PRODUCTS.filter((p) => p.id !== product.id).slice(0, 3);

  return (
    <>
      <SEO
        title={`${product.name} (${CAD(product.price)} CAD) — MyFinancesGuru Tools`}
        description={product.description}
        path={`/products/${product.id}`}
        image={product.image}
        schema={productSchema(product)}
      />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Products", to: "/products" }, { label: product.name, to: `/products/${product.id}` }]} />

        <div className="grid gap-10 lg:grid-cols-2">
          <div className="overflow-hidden rounded-2xl border border-border bg-card">
            <img src={product.image} alt={product.name} className="h-full w-full object-cover" />
          </div>

          <div>
            <div className="text-sm font-semibold uppercase tracking-wide text-action">{product.category}</div>
            <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight text-foreground">{product.name}</h1>
            <p className="mt-3 text-lg text-muted-foreground">{product.tagline}</p>
            <div className="mt-6 font-heading text-4xl font-bold text-brand">{CAD(product.price)} <span className="text-base font-medium text-muted-foreground">CAD</span></div>
            <p className="mt-6 text-base leading-relaxed text-foreground/80">{product.description}</p>

            <ul className="mt-6 grid gap-2 sm:grid-cols-2">
              {product.features.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-foreground/80"><Check className="h-4 w-4 text-brand" /> {f}</li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <BuyButton product={product} label="Buy & download" />
            </div>

            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-2"><Download className="h-4 w-4 text-brand" /> Instant download</span>
              <span className="inline-flex items-center gap-2"><RefreshCw className="h-4 w-4 text-brand" /> Free updates</span>
              <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand" /> Secure checkout by Stripe</span>
            </div>
            <p className="mt-6 text-xs text-muted-foreground">Secure checkout by Stripe. Instant download after payment. Works in Google Sheets, Microsoft Excel and most spreadsheet apps.</p>
          </div>
        </div>

        <section className="mt-16 max-w-3xl">
          <EmailSignup
            source={`product-${product.id}`}
            magnet={`${product.name} — updates`}
            title="Get free updates to this template"
            description="Drop your email and we'll send you improved versions and new Canadian planners as they're released. We store only your email — never your numbers."
          />
        </section>

        <section className="mt-20">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">More planners</h2>
            <Link to="/products" className="inline-flex items-center gap-1 text-sm font-semibold text-brand hover:text-action"><ArrowLeft className="h-4 w-4" /> All products</Link>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {others.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      </div>
    </>
  );
}
